import { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, ListPlus, Info } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

export default function WaitlistModal({ open, propertyId, propertyName, room, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setPhone(""); setError(""); setDone(false); setSubmitting(false); }
  }, [open, propertyId, room?.id]);

  if (!open) return null;

  const close = () => onClose?.();

  const submit = async () => {
    setError("");
    if (!name.trim()) { setError("Isi nama Anda."); return; }
    if (phone.replace(/[^0-9]/g, "").length < 8) { setError("Isi nomor HP yang valid."); return; }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/waitlist", {
        property_id: propertyId,
        room_id: room?.id,
        name: name.trim(),
        phone: phone.trim(),
      });
      setDone(true);
      onSuccess?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/40 p-4" data-testid="waitlist-modal" onClick={close}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-5 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <ListPlus size={18} className="text-primary shrink-0" />
            <div className="min-w-0">
              <h3 className="font-heading text-lg font-semibold">Gabung Waiting List</h3>
              {propertyName && <p className="text-sm text-muted-foreground truncate">{propertyName}</p>}
            </div>
          </div>
          <button onClick={close} data-testid="waitlist-modal-close"
            className="grid place-items-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:bg-secondary/60 transition-colors shrink-0">
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="waitlist-success">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <p className="font-semibold">Anda masuk waiting list!</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Nama Anda telah terdaftar sebagai antrean untuk kamar ini. Pemilik kos atau admin akan menghubungi Anda bila kamar tersedia.
            </p>
            <button onClick={close} data-testid="waitlist-done-button"
              className="mt-5 w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
              Selesai
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {room && (
              <div className="rounded-xl bg-secondary/40 px-4 py-2.5" data-testid="waitlist-room-info">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Kamar</p>
                <p className="mt-0.5 text-sm font-semibold">{room.room_number}{room.name ? ` · ${room.name}` : ""}</p>
              </div>
            )}
            <div className="flex gap-2 rounded-xl bg-primary/5 border border-primary/15 px-4 py-3 text-sm text-muted-foreground">
              <Info size={16} className="text-primary shrink-0 mt-0.5" />
              <p>Kamar ini sedang terisi. Dengan mendaftar, nama Anda tercatat dalam <span className="font-medium text-foreground">waiting list</span> kamar ini dan Anda mungkin akan dihubungi pemilik kos atau admin saat kamar tersedia.</p>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Nama</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="waitlist-name-input"
                placeholder="Nama lengkap Anda"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Nomor HP</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="waitlist-phone-input"
                inputMode="tel" placeholder="mis. 0812xxxxxxxx"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="waitlist-error">{error}</p>}
            <button onClick={submit} disabled={submitting} data-testid="waitlist-submit-button"
              className="w-full py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <ListPlus size={16} />} Gabung Waiting List
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
